import { useCallback } from "react";
import { MessageDTO } from "utils/helpers/models/shared/message.dto";
import useAmbassador from "../../useHooks";
import useStore from "hooks/useStore";
import { concatPathWithBackendUrl } from "utils/helpers/urlHelper";
import dpIcon from "assets/images/others/host-side-images/dpIcon.png";

const useAmbassadorCommunityHooks = () => {
  const { getAmbassadorCommunityMessages, postAmbassadorCommunityMessages } = useAmbassador();
  const { getUserId } = useStore();

  const getCommunityMessages = useCallback(
    async (
      eventId: number,
      queryParams: any,
      setData: Function,
      messages: MessageDTO[],
      setFilters?: Function
    ) => {
      try {
        await getAmbassadorCommunityMessages(
          eventId,
          (apiResponse: any) => {
            if (!apiResponse || !apiResponse.content || !Array.isArray(apiResponse.content)) {
              setData([]);
              return;
            }

            const myUserId = getUserId();
            const dataToSet = apiResponse.content.map((msg: any) => {
              const msgUserProfilePic = msg.user?.profilePic?.name 
                ? concatPathWithBackendUrl(msg.user.profilePic.name)
                : dpIcon;

              return new MessageDTO({
                id: msg.id,
                message: msg.message,
                messageType: msg.message_type,
                createdAt: msg.createdAt,
                eventId: msg.event_id,
                sentByMe: msg.user_id === myUserId,
                userProfilePic: msgUserProfilePic
              });
            });
            
            setData(dataToSet);
          },
          queryParams
        );
      } catch (error) {
        console.error("Error fetching ambassador community messages:", error);
        setData([]);
      }
    },
    [getAmbassadorCommunityMessages, getUserId]
  );

  const sendMessageToCommunity = useCallback(
    async (
      eventId: number,
      message: MessageDTO,
      messages: MessageDTO[],
      setMessages: (msgs: MessageDTO[] | ((prev: MessageDTO[]) => MessageDTO[])) => void
    ) => {
      try {
        const payload = {
          message: message.message,
          eventId: eventId
        };

        const response = await postAmbassadorCommunityMessages(eventId, payload);
        // The polling will pick up the real message and replace the optimistic one
        // If response has data, we can update immediately, otherwise polling will handle it
        if (response?.status && response?.data) {
          // Update all messages to replace the temporary message with the server response
          let replaced = false;
          const updatedMessages = messages.map((msg) => {
            if (msg.id === message.id) {
              replaced = true;
              const msgUserProfilePic = response.data?.user?.profilePic?.name 
                ? concatPathWithBackendUrl(response.data.user.profilePic.name)
                : dpIcon;
              return new MessageDTO({
                id: response.data.id,
                message: response.data.message,
                messageType: response.data.message_type,
                createdAt: response.data.createdAt,
                eventId: response.data.event_id,
                sentByMe: true,
                userProfilePic: msgUserProfilePic
              });
            }
            return msg;
          });
          // If we didn't find the optimistic message for any reason, append the server one
          const finalMessages = replaced
            ? updatedMessages
            : [...messages, new MessageDTO({
                id: response.data.id,
                message: response.data.message,
                messageType: response.data.message_type,
                createdAt: response.data.createdAt,
                eventId: response.data.event_id,
                sentByMe: true,
                userProfilePic: response.data?.user?.profilePic?.name 
                  ? concatPathWithBackendUrl(response.data.user.profilePic.name)
                  : dpIcon
              })];
          setMessages(finalMessages);
        }
        // If no data in response, polling will pick up the message
        return response;
      } catch (error) {
        console.error("Error sending ambassador community message:", error);
        throw error;
      }
    },
    [postAmbassadorCommunityMessages]
  );

  return {
    getCommunityMessages,
    sendMessageToCommunity,
  };
};

export default useAmbassadorCommunityHooks;
